/**
 * Facility-timezone reprojection for appointment status timestamps.
 *
 * Mirrors the legacy `convertDateTimeToTzone(dateTimeStr, fromTzone, toTzone)`
 * helper that ships inline with `html/html/services/scheduler-appt-details.php`
 * (L1134–L1154). Legacy stores typed status timestamps as facility-local
 * wall-clock strings ("Y-m-d g:i A") and re-renders them in the new facility's
 * IANA tz when the user picks a different facility (`updateFacilityDateTime`,
 * L1156–L1188), preserving the underlying real-world instant.
 *
 * v5 stores those same status timestamps as ISO UTC literals where the
 * `slice(0, 16)` portion IS the wall-clock the user sees in the
 * `<input type="datetime-local">` field. Treat that wall-clock as belonging
 * to the *previous* facility's tz, compute the actual UTC instant, then
 * express that instant as wall-clock in the new facility's tz, and finally
 * reassemble as an ISO UTC literal so the rest of the modal continues to
 * round-trip the value via `datetimeLocalValue` / `datetimeLocalToIso`.
 *
 * Reference: docs/audit/appt-details-parity.md §5.C.5 + P4.
 *
 * Every conversion here reads the zone's UTC offset from the server's rules
 * (`lib/tzRules`, SCRUM-2428) and asks the browser's timezone data only for a
 * zone or date those rules don't cover.
 */

import { serverOffsetMinutes, serverZoneAbbreviation } from '@/lib/tzRules';

export interface WallClockComponents {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/**
 * Reproject a v5 ISO-UTC-literal status timestamp from `fromTz` to `toTz`,
 * preserving the underlying real instant. Returns the input untouched when
 * either side is missing/equal or when parsing fails.
 */
export function reprojectIsoBetweenTzs(
  iso: string | null | undefined,
  fromTz: string | null | undefined,
  toTz: string | null | undefined,
): string | null {
  if (!iso) return iso ?? null;
  if (!fromTz || !toTz || fromTz === toTz) return iso;

  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return iso;

  const wall: WallClockComponents = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
  };

  const utcMs = wallClockInTzToUtcMs(wall, fromTz);
  if (utcMs === null) return iso;

  const reprojectedWall = wallClockComponentsInTz(utcMs, toTz);
  if (!reprojectedWall) return iso;

  return formatIsoUtcLiteral(reprojectedWall);
}

/**
 * Given the wall-clock components a user sees inside `tz`, return the actual
 * UTC milliseconds that produce that wall-clock when rendered in `tz`. Uses
 * an iterative correction pass so a 30-minute or fractional offset (e.g.
 * `Asia/Kolkata`, `Australia/Eucla`) converges in at most three rounds.
 */
function wallClockInTzToUtcMs(wall: WallClockComponents, tz: string): number | null {
  const targetWallMs = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
  let utcMs = targetWallMs;

  for (let i = 0; i < 3; i++) {
    const localWall = wallClockComponentsInTz(utcMs, tz);
    if (!localWall) return null;
    const localWallMs = Date.UTC(
      localWall.year,
      localWall.month - 1,
      localWall.day,
      localWall.hour,
      localWall.minute,
    );
    const delta = targetWallMs - localWallMs;
    if (delta === 0) break;
    utcMs += delta;
  }

  return utcMs;
}

/**
 * Render `utcMs` as wall-clock components in `tz`: the server's offset when its
 * rules cover the zone and instant, else `Intl.DateTimeFormat`. Returns `null`
 * if neither can resolve the supplied IANA name.
 */
function wallClockComponentsInTz(utcMs: number, tz: string): WallClockComponents | null {
  const offset = serverOffsetMinutes(tz, utcMs);
  if (offset !== null) {
    const shifted = new Date(utcMs + offset * 60_000);
    return {
      year: shifted.getUTCFullYear(),
      month: shifted.getUTCMonth() + 1,
      day: shifted.getUTCDate(),
      hour: shifted.getUTCHours(),
      minute: shifted.getUTCMinutes(),
    };
  }

  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = fmt.formatToParts(new Date(utcMs));
    const lookup = (type: string): number => {
      const p = parts.find((part) => part.type === type);
      return p ? Number(p.value) : Number.NaN;
    };

    let hour = lookup('hour');
    if (hour === 24) hour = 0;

    const result: WallClockComponents = {
      year: lookup('year'),
      month: lookup('month'),
      day: lookup('day'),
      hour,
      minute: lookup('minute'),
    };

    if (
      Number.isNaN(result.year) ||
      Number.isNaN(result.month) ||
      Number.isNaN(result.day) ||
      Number.isNaN(result.hour) ||
      Number.isNaN(result.minute)
    ) {
      return null;
    }

    return result;
  } catch {
    return null;
  }
}

function formatIsoUtcLiteral(wall: WallClockComponents): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    `${wall.year.toString().padStart(4, '0')}-` +
    `${pad(wall.month)}-${pad(wall.day)}T` +
    `${pad(wall.hour)}:${pad(wall.minute)}:00Z`
  );
}

const PACIFIC_TZ = 'America/Los_Angeles';

/**
 * Get the short timezone abbreviation (e.g. "PST", "PDT", "EST") for an
 * IANA timezone at a given date: the server's name when its rules cover it,
 * else `Intl.DateTimeFormat`'s.
 */
export function getTimezoneAbbreviation(iana: string, date?: Date): string {
  const server = serverZoneAbbreviation(iana, (date ?? new Date()).getTime());
  if (server) return server;
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: iana,
      timeZoneName: 'short',
    });
    const parts = fmt.formatToParts(date ?? new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? iana;
  } catch {
    return iana;
  }
}

/**
 * Render a true-UTC ISO appointment timestamp as wall-clock in the *facility's*
 * timezone, suffixed with that zone's abbreviation ("Aug 12, 2026, 3:00 PM PDT").
 *
 * An appointment time belongs to the clinic, not the viewer: a Pacific staffer
 * looking at an Eastern facility must see the clock the patient will walk in on.
 * Falls back to Pacific (the DB's historical storage zone) when the row carries
 * no facility zone, and returns the raw string when it can't be parsed.
 *
 * `separator` sits between the date and the time — ", " to match the
 * Appointments tab cards, " at " for the sidebar summary cards.
 */
export function formatApptDateTimeInFacilityTz(
  iso: string | null | undefined,
  facilityTz: string | null | undefined,
  separator = ', ',
): string {
  if (!iso) return '';

  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return iso;

  const targetTz = facilityTz || PACIFIC_TZ;
  const utcMs = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
  );

  const wall = wallClockComponentsInTz(utcMs, targetTz);
  if (!wall) return iso;

  // Components are already reprojected into `targetTz`; this Date is only a
  // vehicle for `toLocaleDateString` / `toLocaleTimeString`, built and read in
  // UTC so the browser's own zone (and its daylight-saving gaps) never touch
  // it. The abbreviation, by contrast, is looked up against the real instant
  // so a row near a DST boundary reads PST vs PDT correctly.
  const wallDate = wallClockVehicle(wall);
  const datePart = wallDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const timePart = wallDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
  const tzAbbr = getTimezoneAbbreviation(targetTz, new Date(utcMs));

  return `${datePart}${separator}${timePart} ${tzAbbr}`;
}

/**
 * Convert a Pacific (DB-stored) date + time to wall-clock in the target
 * timezone. Mirrors legacy `formatDateTime()` from
 * `general-date-functions.php` L174-L213.
 *
 * @param pacificDate "YYYY-MM-DD"
 * @param pacificTime "HH:MM"
 * @param targetTz IANA timezone name
 * @returns { date: "YYYY-MM-DD", time: "HH:MM" } in targetTz
 */
export function pacificToWallClock(
  pacificDate: string,
  pacificTime: string,
  targetTz: string,
): { date: string; time: string } {
  if (!pacificDate || !pacificTime || targetTz === PACIFIC_TZ) {
    return { date: pacificDate, time: pacificTime };
  }

  const [y, mo, d] = pacificDate.split('-').map(Number);
  const [h, mi] = pacificTime.split(':').map(Number);

  const wall: WallClockComponents = { year: y, month: mo, day: d, hour: h, minute: mi };
  const utcMs = wallClockInTzToUtcMs(wall, PACIFIC_TZ);
  if (utcMs === null) return { date: pacificDate, time: pacificTime };

  const targetWall = wallClockComponentsInTz(utcMs, targetTz);
  if (!targetWall) return { date: pacificDate, time: pacificTime };

  const pad = (n: number): string => String(n).padStart(2, '0');
  return {
    date: `${targetWall.year.toString().padStart(4, '0')}-${pad(targetWall.month)}-${pad(targetWall.day)}`,
    time: `${pad(targetWall.hour)}:${pad(targetWall.minute)}`,
  };
}

/**
 * Render a Pacific (DB-stored) DATETIME string such as "2026-09-05 14:00:00"
 * or "2026-09-05T14:00" as wall-clock in the viewer's timezone, suffixed with
 * that zone's abbreviation ("Sat, Sep 5, 2026 5:00 PM EDT").
 *
 * Every legacy row datetime is written with `config('app.timezone')` =
 * America/Los_Angeles and carries no offset, so `new Date(str)` would read
 * it as browser-local and silently show the wrong clock to a non-Pacific
 * viewer. Parse it as Pacific, reproject, and label the zone so the reader
 * never has to guess. Returns "—" when the input is empty or unparseable.
 */
export function formatPacificDateTime(
  pacificDateTime: string | null | undefined,
  targetTz: string,
  options: { weekday?: boolean } = {},
): string {
  if (!pacificDateTime || pacificDateTime.startsWith('0000-00-00')) return '—';

  const match = pacificDateTime.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!match) return '—';

  const wall: WallClockComponents = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
  };
  const utcMs = wallClockInTzToUtcMs(wall, PACIFIC_TZ);
  if (utcMs === null) return '—';

  const tz = targetTz || PACIFIC_TZ;
  const targetWall = wallClockComponentsInTz(utcMs, tz);
  if (!targetWall) return '—';

  // A UTC Date built from the reprojected components — only a vehicle for
  // locale formatting, read back in UTC so the browser's own zone never moves
  // it. The abbreviation is looked up against the real instant so rows near a
  // DST boundary read PST vs PDT correctly.
  const wallDate = wallClockVehicle(targetWall);
  if (isNaN(wallDate.getTime())) return '—';

  const datePart = wallDate.toLocaleDateString('en-US', {
    ...(options.weekday ? { weekday: 'short' as const } : {}),
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const timePart = wallDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
  return `${datePart} ${timePart} ${getTimezoneAbbreviation(tz, new Date(utcMs))}`;
}

/**
 * The calendar day a Pacific (DB-stored) DATETIME falls on in `targetTz`
 * ("Oct 6, 2026"), for date-only columns. Taking the first ten characters
 * reads the Pacific day, which is a day early for a late-evening Pacific time
 * seen from further east. Returns "—" when the input is empty or unparseable.
 */
export function formatPacificDate(
  pacificDateTime: string | null | undefined,
  targetTz: string,
): string {
  if (!pacificDateTime || pacificDateTime.startsWith('0000-00-00')) return '—';

  const match = pacificDateTime.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!match) return '—';

  const utcMs = wallClockInTzToUtcMs(
    {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
      hour: Number(match[4]),
      minute: Number(match[5]),
    },
    PACIFIC_TZ,
  );
  if (utcMs === null) return '—';

  const wall = wallClockComponentsInTz(utcMs, targetTz || PACIFIC_TZ);
  if (!wall) return '—';

  return wallClockVehicle(wall).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * A Date whose UTC fields are `wall`, for formatting a clock that is already in
 * its zone with `timeZone: 'UTC'`. A browser-local Date would be moved by the
 * browser's own daylight-saving gaps: one that still expects BC's spring jump
 * turns 2:30 AM on 2027-03-14 into 3:30.
 */
function wallClockVehicle(wall: WallClockComponents): Date {
  return new Date(Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute));
}

/**
 * Render a wall-clock datetime that is *already* expressed in `tz`
 * ("2026-09-14T09:00"), suffixed with that zone's abbreviation.
 *
 * The marketing endpoints convert a campaign's stored Pacific `scheduled_at`
 * into the account's zone themselves and return a bare `Y-m-d\TH:i` string —
 * the shape a `datetime-local` input round-trips. Handing that string to
 * `new Date()` re-reads it in the *browser's* zone, and projecting the result
 * back into the account zone then shifts the clock by the gap between the two:
 * an 8am Pacific send became 9am correctly on the server, then displayed as
 * 7am MDT to a viewer whose computer was on Eastern time. It only looked right
 * when the viewer's computer happened to match the account.
 *
 * The fix is to resolve the wall clock to the instant it denotes *in its own
 * zone*, then render that instant back in that same zone. Round-tripping
 * through the instant is what keeps the browser's zone out of the result
 * entirely, including on a date where the browser's own zone has a daylight
 * saving gap that would swallow the clock time. The zone abbreviation comes
 * from the same instant, so a date either side of a daylight saving change is
 * labelled correctly.
 */
export function formatWallClockInTz(
  wallClockInTz: string | null | undefined,
  tz: string,
  options: { weekday?: boolean } = {},
): string {
  if (!wallClockInTz || wallClockInTz.startsWith('0000-00-00')) return '—';

  const match = wallClockInTz.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!match) return '—';

  const wall: WallClockComponents = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
  };

  const zone = tz || PACIFIC_TZ;
  const utcMs = wallClockInTzToUtcMs(wall, zone);
  if (utcMs === null) return '—';

  return toLocaleStringInTz(utcMs, 'en-US', {
    timeZone: zone,
    ...(options.weekday ? { weekday: 'short' as const } : {}),
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/**
 * Convert a UTC ISO timestamp from the API (e.g. "2024-01-15T15:00:00Z") to
 * wall-clock date + time in the target timezone.
 *
 * @param utcIso ISO 8601 UTC string (with or without trailing "Z")
 * @param targetTz IANA timezone name
 * @returns { date: "YYYY-MM-DD", time: "HH:MM" } in targetTz, or null if parse fails
 */
export function utcToWallClock(
  utcIso: string | null | undefined,
  targetTz: string,
): { date: string; time: string } | null {
  if (!utcIso || !targetTz) return null;

  const match = utcIso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return null;

  const utcMs = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
  );

  const targetWall = wallClockComponentsInTz(utcMs, targetTz);
  if (!targetWall) return null;

  const pad = (n: number): string => String(n).padStart(2, '0');
  return {
    date: `${targetWall.year.toString().padStart(4, '0')}-${pad(targetWall.month)}-${pad(targetWall.day)}`,
    time: `${pad(targetWall.hour)}:${pad(targetWall.minute)}`,
  };
}

/**
 * Reproject a date + time from one IANA timezone to another, preserving
 * the underlying real-world instant. Used when the user switches facility.
 *
 * @param date "YYYY-MM-DD"
 * @param time "HH:MM"
 * @param fromTz source IANA timezone
 * @param toTz target IANA timezone
 * @returns { date: "YYYY-MM-DD", time: "HH:MM" } in toTz
 */
export function reprojectDateTime(
  date: string,
  time: string,
  fromTz: string,
  toTz: string,
): { date: string; time: string } {
  if (!date || !time || !fromTz || !toTz || fromTz === toTz) {
    return { date, time };
  }

  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);

  const wall: WallClockComponents = { year: y, month: mo, day: d, hour: h, minute: mi };
  const utcMs = wallClockInTzToUtcMs(wall, fromTz);
  if (utcMs === null) return { date, time };

  const targetWall = wallClockComponentsInTz(utcMs, toTz);
  if (!targetWall) return { date, time };

  const pad = (n: number): string => String(n).padStart(2, '0');
  return {
    date: `${targetWall.year.toString().padStart(4, '0')}-${pad(targetWall.month)}-${pad(targetWall.day)}`,
    time: `${pad(targetWall.hour)}:${pad(targetWall.minute)}`,
  };
}

// ─── User-timezone display helpers ──────────────────────────────────────
// The scheduler wire format is true-UTC ISO ("...Z"). The browser's
// `new Date(iso)` parses it correctly, but `.getHours()` /
// `.toLocaleString()` defaults to the *browser-local* zone, which may not
// match the user's preferred zone (resolved server-side from
// `users_timezones` with fallback to `aa_accounts.timezone`). The helpers
// below extract date / hour-minute components in an arbitrary IANA zone
// so the SchedulePage day-bucket and visible-window filter render in the
// user's preferred zone instead of the browser's.
//
// Implementation reuses the existing `wallClockComponentsInTz` primitive
// so the date/time math goes through `Intl.DateTimeFormat` and DST rules
// are handled by the runtime.

/**
 * Return the "YYYY-MM-DD" portion of `iso` (a true-UTC ISO with `Z`)
 * as it appears in `tz`. Equivalent to `appt.start.slice(0, 10)` but
 * TZ-aware so events bucket on the *user's* local day, not UTC.
 */
export function dateInTz(iso: string, tz: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const ms = d.getTime();
  if (Number.isNaN(ms)) return iso.slice(0, 10);
  const wall = wallClockComponentsInTz(ms, tz);
  if (!wall) return iso.slice(0, 10);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${wall.year.toString().padStart(4, '0')}-${pad(wall.month)}-${pad(wall.day)}`;
}

/**
 * Today's calendar date ("YYYY-MM-DD") as it reads in `tz`.
 *
 * Use this — never `new Date().toISOString().slice(0, 10)` — for any date
 * that gets *stored* as a business date. `toISOString()` yields the UTC day,
 * so from 4/5 PM Pacific onward it is already tomorrow: a 5:22 PM payment on
 * the 31st would be filed under the 1st.
 */
export function todayInTz(tz: string): string {
  return dateInTz(new Date().toISOString(), tz);
}

/**
 * The UTC instant ("YYYY-MM-DDTHH:mm:00Z") at which calendar date `ymd`
 * begins in `tz` — that zone's local midnight.
 *
 * A bare date sent to `/appointments/calendar` means the *Pacific* day, but
 * the calendar buckets appointments by the viewer's day (`dateInTz`). Send
 * these instants instead so the fetched window is the day the viewer sees —
 * otherwise an Eastern viewer loses everything from 9 PM Pacific onward
 * (SCRUM-2046). Returns null when the date or zone cannot be resolved.
 */
export function startOfDayInTzIso(ymd: string, tz: string): string | null {
  const match = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const utcMs = wallClockInTzToUtcMs(
    { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]), hour: 0, minute: 0 },
    tz,
  );
  if (utcMs === null) return null;
  const utcWall = wallClockComponentsInTz(utcMs, 'UTC');
  return utcWall ? formatIsoUtcLiteral(utcWall) : null;
}

/**
 * Return minutes-since-midnight (0-1439) for `iso` (true-UTC ISO) as it
 * appears in `tz`. Used by the calendar visible-window filter so events
 * line up with the user's wall clock instead of the browser-local one.
 */
export function minutesSinceMidnightInTz(iso: string, tz: string): number {
  if (!iso) return 0;
  const d = new Date(iso);
  const ms = d.getTime();
  if (Number.isNaN(ms)) return 0;
  const wall = wallClockComponentsInTz(ms, tz);
  if (!wall) return 0;
  return wall.hour * 60 + wall.minute;
}

/**
 * Real UTC milliseconds for whole-hour wall-clock `hour` on `dateStr`
 * ("YYYY-MM-DD") as read in `tz`. `hour` may be 24 (or higher) for a window that
 * ends at midnight — it rolls into the following day.
 *
 * Subtracting two of these gives the REAL elapsed minutes across a span, which is
 * 23 or 25 hours on a DST transition day. Returns `null` when `dateStr` is
 * unparseable or the runtime cannot resolve the IANA name.
 */
export function wallClockHourToUtcMs(dateStr: string, hour: number, tz: string): number | null {
  const parts = dateStr.split('-');
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map((p) => Number(p));
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour)
  ) {
    return null;
  }
  return wallClockInTzToUtcMs({ year, month, day, hour, minute: 0 }, tz);
}

/**
 * Return "HH:MM" (24h) for `iso` as it appears in `tz`. Used when the
 * user clicks a find-available slot and we need to seed the
 * create-appointment modal with a 24h time string in the user's TZ.
 */
export function timeInTz(iso: string, tz: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const ms = d.getTime();
  if (Number.isNaN(ms)) return iso.slice(11, 16);
  const wall = wallClockComponentsInTz(ms, tz);
  if (!wall) return iso.slice(11, 16);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(wall.hour)}:${pad(wall.minute)}`;
}

/**
 * Resolve the IANA name to use for client-side formatting. Falls back to
 * the browser's resolved zone when the API didn't supply one (older me
 * payloads, anonymous flows).
 */
export function resolveDisplayTimezone(preferred?: string | null): string {
  if (preferred && preferred.trim() !== '') return preferred;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
  } catch {
    return 'America/Los_Angeles';
  }
}

// ─── Zone-aware formatting on the server's rules (SCRUM-2428) ───────────
// Use these instead of `new Date(x).toLocale*String(locales, { timeZone })`
// and `Intl.DateTimeFormat({ timeZone }).formatToParts`, which read the
// browser's own timezone data: a browser that predates a rule change (BC,
// 2026-11-01) shows every later time off by the change.
//
// A zone name in the output is always the server's short label ("PDT"): any
// `timeZoneName`, and a `timeStyle` of "long" or "full", give the time plus
// that label rather than the browser's long name.

type DateInput = Date | number | string;
type LocaleMethod = 'toLocaleString' | 'toLocaleDateString' | 'toLocaleTimeString';

function formatInTz(
  method: LocaleMethod,
  value: DateInput,
  locales: string | string[] | undefined,
  options: Intl.DateTimeFormatOptions | undefined,
): string {
  const ms = new Date(value).getTime();
  const tz = options?.timeZone;
  const offset = tz ? serverOffsetMinutes(tz, ms) : null;
  if (!tz || offset === null) return new Date(ms)[method](locales, options);

  // Shift the instant by the server's offset and format it as UTC, which no
  // timezone rule can move; the zone name comes from the same rules. Left as
  // they are, "long" and "full" time styles would name the zone "UTC".
  const utcOptions: Intl.DateTimeFormatOptions = { ...options, timeZone: 'UTC' };
  delete utcOptions.timeZoneName;
  const zoneInStyle = utcOptions.timeStyle === 'long' || utcOptions.timeStyle === 'full';
  if (zoneInStyle) utcOptions.timeStyle = 'medium';
  const text = new Date(ms + offset * 60_000)[method](locales, utcOptions);
  return options?.timeZoneName || zoneInStyle ? `${text} ${serverZoneAbbreviation(tz, ms) ?? ''}`.trimEnd() : text;
}

/** `new Date(value).toLocaleString(locales, options)`, `options.timeZone` read on the server's rules. */
export function toLocaleStringInTz(
  value: DateInput,
  locales?: string | string[],
  options?: Intl.DateTimeFormatOptions,
): string {
  return formatInTz('toLocaleString', value, locales, options);
}

/** `new Date(value).toLocaleDateString(locales, options)`, `options.timeZone` read on the server's rules. */
export function toLocaleDateStringInTz(
  value: DateInput,
  locales?: string | string[],
  options?: Intl.DateTimeFormatOptions,
): string {
  return formatInTz('toLocaleDateString', value, locales, options);
}

/** `new Date(value).toLocaleTimeString(locales, options)`, `options.timeZone` read on the server's rules. */
export function toLocaleTimeStringInTz(
  value: DateInput,
  locales?: string | string[],
  options?: Intl.DateTimeFormatOptions,
): string {
  return formatInTz('toLocaleTimeString', value, locales, options);
}

/**
 * The wall-clock parts of an instant in `tz` (month 1–12), on the server's
 * rules where they cover it. Seconds are the same in every zone, so they come
 * straight from the instant. Null when the instant or zone can't be read.
 */
export function wallClockPartsInTz(
  value: DateInput,
  tz: string,
): (WallClockComponents & { second: number }) | null {
  const ms = new Date(value).getTime();
  if (Number.isNaN(ms)) return null;
  const wall = wallClockComponentsInTz(ms, tz);
  return wall ? { ...wall, second: new Date(ms).getUTCSeconds() } : null;
}
